<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { computed, watch } from 'vue';
import ActivityHistoryTimeline from '@/components/activity/ActivityHistoryTimeline.vue';
import ActivityInterruptButton from '@/components/activity/ActivityInterruptButton.vue';
import ActivityProgressBar from '@/components/activity/ActivityProgressBar.vue';
import ActivityStatusBadge from '@/components/activity/ActivityStatusBadge.vue';
import {
    getTypeIcon,
    getTypeLabel,
    isActiveStatus,
} from '@/composables/useActivityLabels';
import type {
    ActivityStatus,
    ActivityType,
} from '@/composables/useActivityLabels';
import { useRunProgress } from '@/composables/useRunProgress';

interface ActivityProgress {
    current?: number;
    total?: number;
    percentage?: number;
    papers_processed?: number;
    included?: number;
    excluded?: number;
    duration_seconds?: number;
    total_cost_usd?: number;
}

interface Activity {
    id: string;
    type: string;
    status: string;
    progress: ActivityProgress | null;
    created_by: { id: string; name: string } | null;
    started_at: string | null;
    completed_at: string | null;
    error: string | null;
    created_at: string;
}

const props = defineProps<{
    activity: Activity;
}>();

const emit = defineEmits<{
    interrupted: [activityId: string];
}>();

// Live progress via Echo (subscribes to run.{id} channel)
const {
    progress: liveProgress,
    events,
    status: liveStatus,
} = useRunProgress(props.activity.id);

// Merge server status with live status (live takes precedence once connected)
const currentStatus = computed<ActivityStatus>(() => {
    const live = liveStatus.value;

    // useRunProgress initialises status to 'pending'; only override once we
    // actually receive an event (i.e. progress or status changed from default)
    if (live !== 'pending' || events.value.length > 0) {
        return live as ActivityStatus;
    }

    return props.activity.status as ActivityStatus;
});

const currentProgress = computed<ActivityProgress | null>(() => {
    if (liveProgress.value > 0) {
        return {
            ...(props.activity.progress ?? {}),
            percentage: liveProgress.value,
        };
    }

    return props.activity.progress;
});

const typeLabel = getTypeLabel(props.activity.type);
const TypeIcon = getTypeIcon(props.activity.type);

const isActive = computed(() => isActiveStatus(currentStatus.value));

const completedStats = computed(() => {
    if (currentStatus.value !== 'completed') {
        return null;
    }

    const p = props.activity.progress;

    if (!p) {
        return null;
    }

    const items: Array<{ label: string; value: string | number }> = [];

    if (p.papers_processed != null) {
        items.push({ label: 'traitées', value: p.papers_processed });
    }

    if (p.included != null) {
        items.push({ label: 'incluses', value: p.included });
    }

    if (p.excluded != null) {
        items.push({ label: 'exclues', value: p.excluded });
    }

    return items.length > 0 ? items : null;
});

function formatDate(iso: string | null): string {
    if (!iso) {
        return '—';
    }

    return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function onInterrupted() {
    emit('interrupted', props.activity.id);
    // Reload only the runs list from server
    router.reload({ only: ['runs'] });
}

// Auto-reload when activity transitions to a terminal status via live events
watch(currentStatus, (status) => {
    if (
        status === 'completed' ||
        status === 'failed' ||
        status === 'cancelled'
    ) {
        router.reload({ only: ['runs'] });
    }
});
</script>

<template>
    <div
        class="rounded-lg border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
    >
        <!-- Header row -->
        <div class="flex items-start justify-between gap-4">
            <!-- Icon + type + meta -->
            <div class="flex min-w-0 items-center gap-3">
                <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
                >
                    <component :is="TypeIcon" class="h-4 w-4" />
                </div>
                <div class="min-w-0">
                    <p class="truncate font-semibold text-foreground">
                        {{ typeLabel }}
                    </p>
                    <p class="text-xs text-muted-foreground">
                        Lancé par {{ activity.created_by?.name ?? 'Système' }} ·
                        {{ formatDate(activity.created_at) }}
                    </p>
                </div>
            </div>

            <!-- Status badge + interrupt button -->
            <div class="flex shrink-0 items-center gap-2">
                <ActivityStatusBadge
                    :status="currentStatus"
                    :type="activity.type as ActivityType"
                />
                <ActivityInterruptButton
                    v-if="isActive"
                    :activity-id="activity.id"
                    @interrupted="onInterrupted"
                />
            </div>
        </div>

        <!-- Progress bar -->
        <ActivityProgressBar
            :progress="currentProgress"
            :status="currentStatus"
        />

        <!-- Error message -->
        <p
            v-if="currentStatus === 'failed' && activity.error"
            class="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
            {{ activity.error }}
        </p>

        <!-- Completion stats -->
        <div v-if="completedStats" class="mt-3 flex gap-4">
            <div
                v-for="stat in completedStats"
                :key="stat.label"
                class="text-center"
            >
                <p class="text-sm font-semibold text-foreground">
                    {{ stat.value }}
                </p>
                <p class="text-xs text-muted-foreground">{{ stat.label }}</p>
            </div>
        </div>

        <!-- Completed date -->
        <p
            v-else-if="currentStatus === 'completed' && activity.completed_at"
            class="mt-3 text-xs text-muted-foreground"
        >
            Terminé le {{ formatDate(activity.completed_at) }}
        </p>

        <!-- Step history timeline -->
        <ActivityHistoryTimeline :events="events" />
    </div>
</template>
